import { useEffect, useRef, useState } from "react";
import EmailAssetToolbar from "@/components/admin/EmailAssetToolbar";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Users, Eye, History, RefreshCw, Mail, AlertTriangle } from "lucide-react";

const SOURCES = ["", "manual", "member", "prospect", "event", "applicant", "import"];

export default function AdminMailshots() {
  return <PortalLayout sidebar="admin"><AdminMailshotsBody /></PortalLayout>;
}

export function AdminMailshotsBody({ hideHeader = false }) {
  const [tab, setTab] = useState("compose"); // compose | history
  const htmlRef = useRef(null);
  const [filter, setFilter] = useState({ source: "", pole: "", tag: "", archived: false });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("<p>Bonjour,</p><p>Voici les dernières nouvelles de La WineTech.</p><p>Cordialement,<br/>L'équipe La WineTech</p>");
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/mailshots").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => { loadHistory(); }, []);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/mailshots/preview", filter);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setPreviewing(false);
  };

  const sendTest = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Sujet et contenu requis"); return; }
    if (!testTo.trim()) { toast.error("Email de test requis"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/mailshots/test", {
        subject, html, filter,
        test_recipients: testTo.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Test envoyé : ${data.sent}/${data.requested}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSending(false);
  };

  const sendCampaign = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Sujet et contenu requis"); return; }
    if (!preview || preview.count === 0) { toast.error("Lance d'abord un aperçu avec au moins 1 destinataire"); return; }
    if (!window.confirm(`Envoyer cette campagne à ${preview.count} destinataire(s) ? Cette action est irréversible.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/mailshots/send", { subject, html, filter });
      toast.success(`Campagne lancée — ${data.recipients_count} destinataires`);
      setTab("history");
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSending(false);
  };

  return (
      <div className="space-y-6">
        {!hideHeader && (
          <div>
            <h1 className="font-serif text-4xl text-navy">Mailshot CRM</h1>
            <p className="mt-1 text-sm text-neutral-600">Envoyer une campagne email à une sélection de contacts (via Brevo).</p>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => setTab("compose")} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === "compose" ? "bg-wine-500 text-white" : "bg-white border border-neutral-200 text-neutral-600"}`} data-testid="tab-compose">Composer</button>
          <button onClick={() => { setTab("history"); loadHistory(); }} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === "history" ? "bg-wine-500 text-white" : "bg-white border border-neutral-200 text-neutral-600"}`} data-testid="tab-history">Historique</button>
        </div>

        {tab === "compose" && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-neutral-100 bg-white p-6 space-y-4">
              <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Users className="h-5 w-5 text-wine-500" /> Cible</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-600">Source</label>
                  <select value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-source">
                    {SOURCES.map((s) => <option key={s} value={s}>{s || "Toutes sources"}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600">Pôle</label>
                  <input value={filter.pole} onChange={(e) => setFilter({ ...filter, pole: e.target.value })} placeholder="ex: innovation_tech" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-pole" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600">Tag</label>
                  <input value={filter.tag} onChange={(e) => setFilter({ ...filter, tag: e.target.value })} placeholder="ex: vip" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="filter-tag" />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={filter.archived} onChange={(e) => setFilter({ ...filter, archived: e.target.checked })} data-testid="filter-archived" />
                    Inclure archivés
                  </label>
                </div>
              </div>
              <button onClick={doPreview} disabled={previewing} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" data-testid="btn-preview">
                <Eye className="h-4 w-4" /> {previewing ? "Calcul..." : "Aperçu destinataires"}
              </button>
              {preview && (
                <div className="rounded-lg bg-wine-50/60 p-4 text-sm" data-testid="preview-result">
                  <div className="font-semibold text-wine-700">{preview.count} destinataire(s)</div>
                  {preview.sample.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-neutral-700">
                      {preview.sample.slice(0, 5).map((c) => (
                        <li key={c.id}>• {c.email} — <span className="text-neutral-500">{c.source || "—"}</span></li>
                      ))}
                      {preview.count > 5 && <li className="italic text-neutral-500">… et {preview.count - 5} autres</li>}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-neutral-100 bg-white p-6 space-y-4">
              <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Mail className="h-5 w-5 text-wine-500" /> Message</h2>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Sujet</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="ex: Newsletter de juin" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="input-subject" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Contenu HTML</label>
                <textarea ref={htmlRef} value={html} onChange={(e) => setHtml(e.target.value)} rows={9} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 font-mono text-xs" data-testid="input-html" />
                <EmailAssetToolbar textareaRef={htmlRef} value={html} onChange={setHtml} testidPrefix="mailshot-asset" />
              </div>
              <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 inline-flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Le contenu sera enveloppé dans le gabarit graphique La WineTech. Pensez à inclure un lien de désinscription si requis par la juridiction.</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-100">
                <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email de test (ou liste séparée par virgules)" className="flex-1 min-w-[200px] rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="input-test-to" />
                <button onClick={sendTest} disabled={sending} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50" data-testid="btn-send-test">
                  <Send className="h-4 w-4" /> Envoyer test
                </button>
                <button onClick={sendCampaign} disabled={sending || !preview || preview.count === 0} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="btn-send-campaign">
                  <Send className="h-4 w-4" /> Envoyer campagne
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><History className="h-5 w-5 text-wine-500" /> Historique des campagnes</h2>
              <button onClick={loadHistory} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50" data-testid="btn-refresh-history">
                <RefreshCw className="h-3 w-3" /> Rafraîchir
              </button>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-neutral-500">
                  <tr><th className="py-2 pr-3">Titre</th><th className="py-2 pr-3">Destinataires</th><th className="py-2 pr-3">Envoyés</th><th className="py-2 pr-3">Échoués</th><th className="py-2 pr-3">Statut</th><th className="py-2 pr-3">Date</th></tr>
                </thead>
                <tbody>
                  {history.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-neutral-500">Aucune campagne envoyée pour le moment.</td></tr>}
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-neutral-100" data-testid={`campaign-row-${h.id}`}>
                      <td className="py-2 pr-3 font-medium text-neutral-800">{h.title}</td>
                      <td className="py-2 pr-3">{h.recipients_count}</td>
                      <td className="py-2 pr-3 text-emerald-600 font-semibold">{h.sent}</td>
                      <td className="py-2 pr-3 text-red-600 font-semibold">{h.failed}</td>
                      <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${h.status === "sent" ? "bg-emerald-100 text-emerald-700" : h.status === "running" ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-700"}`}>{h.status}</span></td>
                      <td className="py-2 pr-3 text-xs text-neutral-500">{h.created_at && new Date(h.created_at).toLocaleString("fr")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
  );
}
